<template>
  <div class="vuetify-parent">
    <v-app>
      <res-loading v-if="!appLoaded"></res-loading>
      <div v-if="appLoaded">
        <res-dialog :title="expiration.title" :showDialog="expiration.showExpiringWarning" :saveButtonOnly="true" saveButtonText="OK" @save="resetIdleCookie" titleClass="resBlue darken-1 white--text" saveButtonColor="resBlue darken-1">
          <v-card-text>
            <span class="font-weight-500">
              {{expiration.message}}
            </span>
          </v-card-text>
        </res-dialog>
        <error-dialog></error-dialog>
        <top-nav></top-nav>
        <res-alert v-model="alertText"></res-alert>
        <left-nav></left-nav>
        <v-main>
          <v-container fluid>
            <router-view></router-view>
          </v-container>
        </v-main>
      </div>
    </v-app>
  </div>
</template>

<script>
  import ResDialog from './components/base/ResDialog';
  import ResLoading from './components/base/ResLoading';
  import ResAlert from './components/base/ResAlert';
  import ErrorDialog from './components/base/dialogs/ErrorDialog';
  import TopNav from './components/navigation/top-navigation/TopNav';
  import LeftNav from './components/navigation/left-navigation/LeftNav';
  import Mgr from './services/auth';
  import helpService from './services/help';
  import { mapActions } from 'vuex';

  export default {
    name: 'App',
    components: {
      ResDialog,
      ResLoading,
      ResAlert,
      ErrorDialog,
      TopNav,
      LeftNav
    },
    data: () => ({
      appLoaded: false,
      autoRefresh: true, 
      mgr: new Mgr(),
      expiration: {
        showExpiringWarning: false,
        title: 'Session Expiring',
        message: '',
        messageFormat: 'For security, your session will expire in =n= seconds due to inactivity. Click OK if you wish to stay logged in.',
        idleWarningBuffer: 60000,
        timeoutId: null
      },
      signedIn: false,
      alertText: '',
    }),
    methods: {
      ...mapActions(['changeUser']),
      async signIn() {
        await this.mgr.getSignedIn()
          .then(signIn => {
            if (signIn) {
              this.mgr.getUser()
                .then(user => {
                  if (user && !user.expired) {
                    this.signedIn = signIn;
                    this.changeUser(user);
                    this.appLoaded = true;
                  }
                })
            }
          })
          .catch(err => {
            console.log(err);
          })
      },
      signOut() {
        window.location.reload();
        this.mgr.signOut();
      },
      promptTokenExpiring() {
        this.beginExpirationCountdown();
      },
      promptTokenExpired() {
        this.expiration.showExpiringWarning = true;
        this.signedIn = false;
      },
      beginExpirationCountdown() {
        this.expiration.showExpiringWarning = true;
        this.expiration.bufferRemaining = this.expiration.idleWarningBuffer;
        var bufferIncrement = 1000; // 1 second
        function countDownIncrement() {
          // if the warning was cleared abort the countdown
          if (!this.expiration.showExpiringWarning) {
            return;
          }

          if (this.expiration.bufferRemaining > 0) {
            this.expiration.message = this.expiration.messageFormat.replace('=n=', this.expiration.bufferRemaining / 1000);
            this.expiration.bufferRemaining -= bufferIncrement;
            setTimeout(countDownIncrement.bind(this), bufferIncrement);
          }
          else {
            this.signOut();
          }
        }
        countDownIncrement.bind(this)();
      },
      mouseActivity() {
        if (this.expiration.showExpiringWarning) {
          return;
        }

        // if expired sign out now
        if (this.idleCookieExpirationTime < +(new Date())) {
          this.signOut();
        }
        else {
          // otherwise renew cookie because there was UI activity
          this.resetIdleCookie();

          // and schedule the countdown to begin
          if (this.expiration.timeoutId) {
            clearTimeout(this.expiration.timeoutId)
          }
          var timeToExpireWarning = this.idleCookieExpirationTime - +(new Date()) - this.expiration.idleWarningBuffer;
          if (timeToExpireWarning) {
            this.expiration.timeoutId = setTimeout(this.beginExpirationCountdown, timeToExpireWarning);
          }
        }
      },
      resetIdleCookie() {
        var self = this;
        self.expiration.showExpiringWarning = false;
        var expireTimeMS = new Date().getTime() + self.idleCookieDuration; //this is the milliseconds since epoch time.
        if (isNaN(expireTimeMS) || isNaN(self.idleCookieDuration)) {
          return;
        }
        var cookieString = "IdleCookie=et[" + expireTimeMS + "]et&icd[" + self.idleCookieDuration + "]icd;expires=" + new Date(expireTimeMS).toUTCString();

        if (location.hostname != "localhost")
        {
          cookieString = cookieString + ";domain=.myresman.com;secure;SameSite=None";
        }

        self.initIdleCookie(cookieString);
      },
      getCleanIdleCookie() {
        return (document.cookie || '').replace('IdleCookie=et[NaN]et&icd[NaN]icd; ', '');
      },
      /// This is an idle timeout due to lack of screen activity defined in ResMan settings, it is unrelated to auth access token timeout handling
      initIdleCookie(cookieString) {
        var cleanCookie = cookieString || this.getCleanIdleCookie();
        var defaultTimeoutDuration = 600 * 1000;//if we don't have an idle duration from the core app cookie, use 10 minutes

        var timeStartIndex = cleanCookie.indexOf("et[") + 3;
        var timeEndIndex = cleanCookie.indexOf("]et");

        this.idleCookieExpirationTime = Number(cleanCookie.substring(timeStartIndex, timeEndIndex)) || (+(new Date()) + (defaultTimeoutDuration))
        
        var durationStartIndex = cleanCookie.indexOf("icd[") + 4;
        var durationEndIndex = cleanCookie.indexOf("]icd");

        this.idleCookieDuration = Number(cleanCookie.substring(durationStartIndex, durationEndIndex)) || (defaultTimeoutDuration);
      },
      directToHelp(tag) {
        helpService.redirectToCommunities(tag);
      },
      showAlert(text) {
        this.alertText = text;
      }
    },
    computed: {

    },
    async created() {
      this.$eventHub.$on('logout', this.signOut);
      this.$eventHub.$on('directToHelp', this.directToHelp);
      this.$eventHub.$on('showAlertNotification', this.showAlert);
      window.addEventListener('tokenExpiring', () => {
        if (this.autoRefresh) {
          this.mgr.renewToken();
        } else {
          this.promptTokenExpiring();
        }
      });
      window.addEventListener('tokenExpired', () => {
        this.promptTokenExpired();
      });
      window.addEventListener('userSignedOut', () => {
        this.clickTimerActivatable = false;
        window.location.reload();
      });
      setTimeout(() => {
        this.signIn();
      }, 1000);
      this.initIdleCookie();
      this._mouseActivity = this._.debounce(this.mouseActivity, 2000, true);
    },
    async mounted() {
      document.addEventListener('click', this._mouseActivity);
      document.addEventListener('mousemove', this._mouseActivity);
    },
    async beforeDestroy() {
      document.removeEventListener('click', this._mouseActivity);
      document.removeEventListener('mousemove', this._mouseActivity);
    },
  };
</script>
<style>
  .v-application {
    max-width: 1800px;
    margin: auto;
  }
</style>

